import { ArrowLeft, Globe2, Mail } from "lucide-react";

type LegalKind = "privacy" | "terms" | "support" | "accessibility";
type LegalSection = {
  title: string;
  paragraphs: string[];
  items?: string[];
};
type LegalContent = {
  label: string;
  title: string;
  intro: string;
  sections: LegalSection[];
};

export function LegalPage({ kind }: { kind: LegalKind }) {
  const content: LegalContent = kind === "privacy"
    ? privacyContent
    : kind === "terms"
      ? termsContent
      : kind === "support"
        ? supportContent
        : accessibilityContent;

  return (
    <main className="legal-page">
      <header className="site-header">
        <a className="brand" href="/" aria-label="Earth Relay home">
          <span className="brand-mark"><Globe2 size={21} /></span>
          <span>EARTH RELAY</span>
        </a>
        <a className="button button--small" href="/">
          <ArrowLeft size={15} /> WORLD MAP
        </a>
      </header>

      <article className="legal-document">
        <div className="legal-eyebrow">EARTH RELAY · {content.label}</div>
        <h1>{content.title}</h1>
        <p className="legal-updated">Effective and last updated: July 31, 2026</p>
        <p className="legal-intro">{content.intro}</p>

        {content.sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {section.items ? (
              <ul>
                {section.items.map((item) => <li key={item}>{item}</li>)}
              </ul>
            ) : null}
          </section>
        ))}

        <aside className="legal-contact">
          <Mail size={22} />
          <div>
            <strong>Questions or privacy requests</strong>
            <a href="mailto:support@talkape.net">support@talkape.net</a>
          </div>
        </aside>
      </article>

      <footer>
        <a className="brand" href="/"><span className="brand-mark"><Globe2 size={21} /></span>EARTH RELAY</a>
        <p>TalkApe (Hangzhou) Technology Co., Ltd.</p>
        <div>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/support">Support</a>
          <a href="/accessibility">Accessibility</a>
        </div>
      </footer>
    </main>
  );
}

const privacyContent: LegalContent = {
  label: "PRIVACY",
  title: "Privacy Policy",
  intro:
    "Earth Relay turns a deliberately started walk into shared, approximate progress. This policy explains what leaves your device, what the public sees, and the controls available to you.",
  sections: [
    {
      title: "1. Who operates Earth Relay",
      paragraphs: [
        "Earth Relay is operated by TalkApe (Hangzhou) Technology Co., Ltd. (对话猿（杭州）科技有限公司), unified social credit code 91330110MA8GE4F33H, Room 105-A0084, Building 21, No. 1399 Liangmu Road, Cangqian Subdistrict, Yuhang District, Hangzhou, Zhejiang 311121, China."
      ]
    },
    {
      title: "2. Data we collect",
      paragraphs: [
        "We collect only the data needed to validate a walk, place its contribution, operate the shared map, prevent duplicate submissions, and answer support requests."
      ],
      items: [
        "Active-walk location evidence: latitude, longitude, horizontal accuracy, speed, timestamp, and the iOS simulated-location signal.",
        "Fitness data: step count and verified walking distance for the session.",
        "Gameplay contribution data: city attribution, approximate H3 cells, duration, campaign, risk result, and contributor sequence number.",
        "Installation identifier: a random identifier stored in the iOS Keychain. We do not require a name, phone number, email address, contacts, or advertising identifier.",
        "Device-integrity data: an Apple App Attest public-key identifier, signed assertions, attestation receipt, executable category, app build version, and replay counter. App Attest does not provide us with your Apple ID.",
        "Invitation attribution: an opaque invitation code, aggregate landing-page click count, aggregate App Store handoff count, and whether a different installation later completed a verified walk. App Store handoff totals store no browser or installation identifier. We do not store an invitee’s contact list or message recipients.",
        "First-party product and performance events: app open, world load, permission-primer view, bucketed walk outcome, bucketed share-video render time and file size, and whether the system share flow completed. Analytics never includes route points, coordinates, message content, contacts, or an advertising identifier.",
        "Support data: anything you choose to include when emailing support."
      ]
    },
    {
      title: "3. When collection starts",
      paragraphs: [
        "Location and motion collection begins only after you tap the start control and grant iOS permission. Earth Relay does not run passive all-day location tracking. An active session may continue while the app is in the background so the walk does not break when the screen locks."
      ]
    },
    {
      title: "4. What the public sees",
      paragraphs: [
        "The public map shows community-level city pulses, city totals, and deliberately coarse cells. It does not publish your precise route, installation identifier, step-by-step timeline, or a public personal profile."
      ]
    },
    {
      title: "5. Purposes and legal bases",
      paragraphs: [
        "We use walk and installation data to provide the service, validate fair movement, attribute a nearby city, prevent replay and abuse, maintain security, and respond to requests. We use coarse first-party events to measure the start-to-walk-to-share funnel and reliability. The analytics subject is a server-keyed, non-reversible digest of the random installation identifier; no third-party analytics or advertising SDK receives it. Depending on where you live, these activities rely on performance of the service you request, our legitimate interests in operating and improving a fair and secure product, and consent through iOS permission controls where required."
      ]
    },
    {
      title: "6. Retention",
      paragraphs: [
        "Raw route evidence is scheduled for deletion no later than 24 hours after validation. One-time App Attest challenges expire after five minutes; the registered integrity key and receipt remain until this installation’s contribution data is deleted. Invitation links expire after 30 days, and unopened attribution saved on an iPhone expires after seven days. First-party product events expire after 90 days. Qualified contribution records and approximate cells remain while needed to operate the campaign and your private in-app passport. You can delete submitted walks, device-integrity records, invitation records, and linked first-party events from Profile → Privacy & your data. Server backups may retain deleted material for a limited disaster-recovery cycle before rotation."
      ]
    },
    {
      title: "7. Sharing and international processing",
      paragraphs: [
        "We use infrastructure providers to host the API, database, website, security logs, and backups. Providers process data under our instructions and may operate in countries other than yours. We do not sell personal data, use walk data for cross-company advertising, or expose it to data brokers. We may disclose information when required by law or to protect the service and people."
      ]
    },
    {
      title: "8. Your choices and rights",
      paragraphs: [
        "You can stop a walk, revoke Location or Motion permission in iOS Settings, export a portable JSON copy from Profile → Privacy & your data, delete contribution data in the app, and contact us to request correction, restriction, or objection where applicable. The export contains submitted walks, any raw route evidence still inside its 24-hour window, invitation records, linked first-party events, non-secret device-integrity metadata, and the private passport stored on that iPhone. You may also complain to your local data-protection authority."
      ]
    },
    {
      title: "9. Children",
      paragraphs: [
        "Earth Relay is not directed to children under 13. Where local law requires a higher age for independent consent, a parent or guardian must approve use. Do not submit a walk if you are not eligible in your location."
      ]
    },
    {
      title: "10. Security and changes",
      paragraphs: [
        "We use transport encryption, restricted database access, short raw-evidence retention, coarse public geography, rate limits, one-time challenges, Apple App Attest assertions, and server-side movement validation. No system is risk-free. We will post material policy changes here and update the effective date."
      ]
    }
  ]
};

const termsContent: LegalContent = {
  label: "TERMS",
  title: "Terms of Service",
  intro:
    "These terms govern Earth Relay and its time-limited community walks. By starting or submitting a walk, you agree to these rules.",
  sections: [
    {
      title: "1. The service",
      paragraphs: [
        "Earth Relay is a cooperative map experience. Virtual lights, cells, levels, badges, city totals, and contributor numbers are expressive game elements. They do not create ownership, political claims, property rights, currency, or entitlement to any physical place or reward."
      ]
    },
    {
      title: "2. Eligibility",
      paragraphs: [
        "You must be at least 13 and legally able to agree to these terms. If local law requires guardian consent, your parent or guardian must approve your use."
      ]
    },
    {
      title: "3. Walk safely",
      paragraphs: [
        "Stay aware of traffic, weather, terrain, and other people. Use lawful public routes, follow local rules, and do not trespass, enter restricted areas, drive while recording, or look at the screen when doing so would be unsafe. Stop the session whenever conditions are unsuitable."
      ]
    },
    {
      title: "4. Fair participation",
      paragraphs: [
        "Submit only your own real walking activity. Do not fabricate locations, replay routes, automate submissions, interfere with validation, overload the service, impersonate others, or use Earth Relay to harass, discriminate, or coordinate harm. We may reject, remove, or rate-limit suspicious contributions."
      ]
    },
    {
      title: "5. Your contribution",
      paragraphs: [
        "You retain rights in material you create. You grant us a worldwide, non-exclusive, royalty-free license to process submitted walk data and any share card you choose to publish solely to operate, secure, explain, and promote Earth Relay. The Privacy Policy limits how location and fitness data are handled."
      ]
    },
    {
      title: "6. Availability and changes",
      paragraphs: [
        "Campaign timers, totals, map rendering, eligibility thresholds, and features may change to preserve fairness, safety, performance, or legal compliance. We may pause or end a campaign or the service. We do not promise uninterrupted availability or preservation of every virtual item."
      ]
    },
    {
      title: "7. Intellectual property",
      paragraphs: [
        "Earth Relay software, brand, artwork, interfaces, and original content belong to TalkApe or its licensors. Geographic names and open datasets remain subject to their respective terms. City attribution is derived in part from GeoNames data."
      ]
    },
    {
      title: "8. Disclaimers and liability",
      paragraphs: [
        "To the extent permitted by law, the service is provided as available without implied warranties. We are not responsible for unsafe route choices, third-party conduct, network outages, inaccurate device signals, or losses that could not reasonably be foreseen. Nothing in these terms limits non-waivable consumer rights or liability that law does not permit us to exclude."
      ]
    },
    {
      title: "9. Suspension and termination",
      paragraphs: [
        "You may stop using Earth Relay and delete contribution data at any time. We may restrict participation for serious or repeated violations. Sections that by nature should survive termination continue to apply."
      ]
    },
    {
      title: "10. Governing terms",
      paragraphs: [
        "These terms are governed by the laws applicable to the operator, without removing mandatory protections available in your home jurisdiction. Before starting formal proceedings, contact support so we can try to resolve the issue promptly."
      ]
    }
  ]
};

const supportContent: LegalContent = {
  label: "SUPPORT",
  title: "Help & Support",
  intro:
    "Tell us what happened and include the app version, iPhone model, and approximate time of the issue. Do not email a precise route, password, identity document, or payment information.",
  sections: [
    {
      title: "Walking and location",
      paragraphs: [
        "Start from the in-app privacy primer and choose Allow While Using App. Keep Precise Location enabled during the active session. If the signal is weak, move to an open area and wait for the GPS status to turn trusted."
      ]
    },
    {
      title: "A saved walk did not upload",
      paragraphs: [
        "Keep the app installed, reconnect to the internet, reopen the saved session, and tap Try Again. Idempotency protection keeps retries from counting the same submitted walk twice."
      ]
    },
    {
      title: "Privacy or deletion request",
      paragraphs: [
        "Open Profile → Privacy & your data → Export my personal data to create a portable JSON file, or choose Delete my contribution data to remove the installation’s submitted data. For additional rights requests, email support from the address below and describe the request. Because guest use does not require an email account, we may ask for limited in-app verification rather than identity documents."
      ]
    },
    {
      title: "Response target",
      paragraphs: [
        "We aim to acknowledge support and privacy requests within five business days. Statutory response periods continue to apply where required."
      ]
    }
  ]
};

const accessibilityContent: LegalContent = {
  label: "ACCESSIBILITY",
  title: "Accessibility",
  intro:
    "Earth Relay is designed so the common journey—from understanding the world mission to recording and sharing a walk—remains usable with iPhone accessibility settings.",
  sections: [
    {
      title: "Supported accessibility features",
      paragraphs: [
        "The current iPhone release supports the following App Store Accessibility Nutrition Label features."
      ],
      items: [
        "VoiceOver: controls, live metrics, city beacons, contribution rows, and charts provide meaningful labels, values, and grouping.",
        "Voice Control: primary actions use native controls with concise spoken labels.",
        "Larger Text: Dynamic Type scales primary copy beyond 200%, and dense horizontal layouts reflow vertically at accessibility sizes.",
        "Dark Interface: every app screen uses a complete dark color scheme.",
        "Differentiate Without Color Alone: text, symbols, shape, and state labels accompany color-coded status.",
        "Sufficient Contrast: primary text and controls use high-contrast moon-white, gold, and cyan against deep navy surfaces.",
        "Reduced Motion: spinning, scaling, and progress animations stop or dissolve when Reduce Motion is enabled."
      ]
    },
    {
      title: "Testing scope",
      paragraphs: [
        "We test the core home, walk, completion, map, impact, profile, privacy, export, and deletion journeys in English, Simplified Chinese, and Traditional Chinese. Automated UI coverage includes the largest iOS accessibility text size with reduced motion."
      ]
    },
    {
      title: "Share videos",
      paragraphs: [
        "The personalized eight-second share video contains no spoken dialogue or essential audio. The same city, contribution, distance, step, and map information is available as accessible text in the completion screen."
      ]
    },
    {
      title: "Feedback",
      paragraphs: [
        "If an assistive-technology workflow does not work as expected, email support@talkape.net with the iPhone model, iOS version, accessibility feature, app version, and the step where the issue occurred. Do not include a precise route."
      ]
    }
  ]
};
